// API Base URL - 항상 상대 경로 사용
// 개발환경: Vite proxy가 /api → api:3000 으로 프록시
// 운영환경: Nginx가 /api → API 서버로 프록시
export const API_URL = '';

// API 호출 헬퍼 함수
export async function api<T = any>(
  endpoint: string,
  options: RequestInit = {}
): Promise<{ success: boolean; data?: T; error?: string }> {
  const token = localStorage.getItem('auth_token');

  try {
    const response = await fetch(`${API_URL}${endpoint}`, {
      ...options,
      headers: {
        'Content-Type': 'application/json',
        ...(token && { 'Authorization': `Bearer ${token}` }),
        ...options.headers,
      },
    });

    // 401 에러 (인증 실패) 처리
    if (response.status === 401) {
      // 로그인 API는 401 시 서버 에러 메시지를 그대로 반환
      const isLoginEndpoint = endpoint.includes('/auth/login');

      if (isLoginEndpoint) {
        const data = await response.json();
        throw new Error(data.error || '이메일 또는 비밀번호가 올바르지 않습니다.');
      }

      // 공개 페이지 목록 (로그인 없이 접근 가능)
      const currentPath = window.location.pathname;
      const publicPaths = ['/', '/login', '/stores', '/store'];
      const isPublicPage = publicPaths.some(path =>
        currentPath === path || currentPath.startsWith('/store/')
      );

      // 공개 페이지에서는 세션 만료 알림 표시하지 않음
      if (!isPublicPage) {
        localStorage.removeItem('auth_token');
        alert('세션이 만료되었습니다. 다시 로그인해주세요.');
        window.location.href = '/login';
      }

      throw new Error('인증이 필요합니다.');
    }

    // 403 에러 (권한 없음) 처리
    if (response.status === 403) {
      throw new Error('접근 권한이 없습니다.');
    }

    const data = await response.json();

    if (!response.ok) {
      throw new Error(data.error || data.message || '요청에 실패했습니다.');
    }

    return data;
  } catch (error: any) {
    if (error.message.includes('Failed to fetch')) {
      throw new Error('서버와의 연결에 실패했습니다.');
    }
    throw error;
  }
}

// 편의 메서드
export const apiGet = <T = any>(endpoint: string) => api<T>(endpoint, { method: 'GET' });

export const apiPost = <T = any>(endpoint: string, body: any) =>
  api<T>(endpoint, { method: 'POST', body: JSON.stringify(body) });

export const apiPut = <T = any>(endpoint: string, body: any) =>
  api<T>(endpoint, { method: 'PUT', body: JSON.stringify(body) });

export const apiDelete = <T = any>(endpoint: string) =>
  api<T>(endpoint, { method: 'DELETE' });

// ========== Types ==========

export interface User {
  id: string;
  email: string;
  name: string;
  phone?: string;
  role: 'super_admin' | 'store_manager';
  created_at: string;
  updated_at?: string;
  assigned_stores_count?: number;
}

export interface Store {
  id: string;
  slug?: string;
  name: string;
  address: string;
  phone: string;
  hours?: string;
  description?: string;
  image?: string;
  kiosk_code?: string;
  kiosk_type_id?: string;
  owner_name?: string;
  business_image?: string;
  room_layout_image?: string;
  unmanned_start_date?: string;
  memo?: string;
  main_door_enabled: boolean;
  cctv_enabled: boolean;
  cctv_nvr_host?: string;
  cctv_nvr_port?: number;
  cctv_nvr_user?: string;
  cctv_nvr_password?: string;
  total_rooms: number;
  latitude?: number;
  longitude?: number;
  created_at: string;
  updated_at?: string;
  activated_at?: string;  // 키오스크 설치 인증 완료 일시
  operational_modified_at?: string;
  facility_modified_at?: string;
  actual_rooms_count?: number;
  // 최신 키오스크 인증 정보
  latest_activation?: {
    activated_at: string;
    os_version?: string;
    app_version?: string;
    mac_address?: string;
    ip_address?: string;
    rustdesk_id?: string;
    hostname?: string;
    cpu_model?: string;
    ram_total?: number;
    storage_total?: number;
    screen_resolution?: string;
    display_count?: number;
    device_id?: string;
  } | null;
  // 필수 설정 미완료 항목 (목록 조회 시만)
  setup_issues?: string[];
  // 키오스크 실시간 상태
  kiosk_status?: {
    online: boolean;
    last_seen: string | null;
    ip_address: string | null;
    app_version: string | null;
    kiosk_code: string | null;
    last_operational_sync_at: string | null;
    last_facility_sync_at: string | null;
  } | null;
}

export interface Room {
  id: string;
  store_id: string;
  room_number: number;
  name: string;
  status: 'available' | 'in_use';
  enabled?: boolean;  // 관리자 수동 사용중지 (false = 이용 불가)
  temperature?: number;
  humidity?: number;
  brightness?: number;
  smoking?: boolean;
  smoke_level?: number;
  projector: boolean;
  aircon: boolean;
  image?: string | null; // 룸 이미지 경로
  facilities?: string | null; // 시설정보 (쉼표 구분 태그)
  cctv_channel?: number | null;
  projector_model_id?: string;
  aircon_model_id?: string;
  room_edge_type_id?: string;
  projector_model?: { name: string; model: string };
  aircon_model?: { name: string; model: string };
  room_edge_model?: { name: string; model: string };
  edge_version?: string;
  last_comm_time?: string;
  created_at: string;
  updated_at?: string;
}

export interface KioskType {
  id: string;
  name: string;
  model: string;
  manufacturer?: string;
  description?: string;
  created_at: string;
}

export interface RoomEdgeType {
  id: string;
  name: string;
  model: string;
  manufacturer?: string;
  description?: string;
  created_at: string;
}

export interface DeviceModel {
  id: string;
  device_type: 'projector' | 'aircon';
  name: string;
  model: string;
  manufacturer?: string;
  remote_data?: Record<string, string>;
  created_at: string;
}

export interface Notice {
  id: string;
  store_id: string;
  title: string;
  content: string;
  created_by?: string;
  created_at: string;
  updated_at?: string;
}

export interface Pricing {
  id: string;
  store_id?: string;
  day_type: 'all' | 'weekday' | 'weekend' | 'holiday' | 'custom';
  time_slot: 'all_day' | 'daytime' | 'nighttime';
  start_hour: number;
  end_hour: number;
  price_per_hour: number;
  display_name?: string;
  created_at?: string;
  updated_at?: string;
}

export interface StoreType {
  id: string;
  name: string;
  image?: string;
  is_active: boolean;
  display_order?: number;
  created_at: string;
}

export interface StoreManager {
  id: string;
  user_id: string;
  store_id: string;
  permissions: {
    read: boolean;
    write: boolean;
    control: boolean;
  };
  created_at: string;
}

// MQTT / 키오스크 관련 타입
export interface KioskStatus {
  id: string;
  store_id: string;
  store_name?: string;
  kiosk_code: string | null;
  rustdesk_id: string | null;
  ip_address: string | null;
  cpu_usage: number | null;
  ram_usage: number | null;
  ram_used: number | null;     // bytes
  ram_total: number | null;    // bytes
  storage_usage: number | null;
  storage_used: number | null;  // bytes
  storage_total: number | null; // bytes
  volume: number;
  room_pc_status?: Array<{
    room_pc_id: string;
    room_number: number;
    is_connected: boolean;
    ip_address?: string;
    mac_address?: string;
    app_version?: string;
  }> | null;
  room_edge_status?: Array<{
    room_number: number;
    is_connected: boolean;
    firmware_version?: string;
    uptime_seconds?: number;
    last_seen?: string;
  }> | null;
  app_version: string | null;
  os_boot_time: string | null;
  app_start_time: string | null;
  online: boolean;
  last_seen: string | null;
  created_at: string;
  updated_at: string;
}

export interface KioskEvent {
  id: string;
  store_id: string;
  store_name?: string;
  room_id: string | null;
  room_name?: string | null;
  event_type: string;
  event_data: Record<string, any>;
  created_at: string;
}

export interface MqttCommandRequest {
  store_id: string;
  room_id?: string;
  command: string;
  action: string;
  params?: Record<string, any>;
}

export interface MqttNotifyRequest {
  store_id: string;
  notify_type: 'config_updated' | 'firmware_available';
  data?: Record<string, any>;
}

// 예약 관련 타입 (API 응답은 snake_case)
export interface StoreSettings {
  id: number;
  store_id: string;
  game_minutes: number;
  practice_minutes: number;
  bonus_calculation: 'per_game' | 'per_session'; // per_game: 게임당 보너스, per_session: 결제당 보너스
  platform_fee_rate: number;
  reservation_enabled: boolean;
  advance_booking_days: number;
  free_reservation_allowed?: boolean;
  cancellation_minutes?: number;
  // 게임 라이프사이클 설정
  pre_warm_minutes?: number;    // 사전 준비 시간 (분, 기본 10)
  // 시간대 구분 설정
  daytime_enabled?: boolean;    // 주간/야간 구분 사용 (기본 true, false면 종일)
  daytime_start_hour?: number;  // 주간 시작 (기본 6)
  daytime_end_hour?: number;    // 주간 종료 (기본 18)
  settlement_cycle?: string;
  settlement_day?: number;
  uses_global_settlement?: boolean;
  created_at?: string;
  updated_at?: string;
}

export interface TimeSlot {
  startTime: string;
  endTime: string;
  available: boolean;
  roomId?: string;
  roomName?: string;
}

// 실제 API 응답 구조에 맞춤
export interface AvailabilityResponse {
  store: {
    id: string;
    name: string;
  };
  date: string;
  slotMinutes: number;
  gameMinutes: number;
  practiceMinutes: number;
  // 제외 시간 정보 (예: "09:00", "20:00")
  excludedStart: string | null;
  excludedEnd: string | null;
  availability: Array<{
    room: {
      id: string;
      name: string;
      roomNumber: number;
    };
    availableSlots: Array<{
      startTime: string;
      endTime: string;
    }>;
  }>;
}

// 경량화된 스케줄 API 응답 (서버: 예외 데이터만, 프론트: 슬롯 생성)
export interface ScheduleResponse {
  store: {
    id: string;
    name: string;
    roomLayoutImage?: string | null;
  };
  date: string;
  isToday: boolean;
  isClosed: boolean;
  message?: string; // 휴무일 등 메시지

  // 게임 설정
  gameMinutes: number;
  practiceMinutes: number;
  slotMinutes: number;
  bonusCalculation: 'per_game' | 'per_session';
  advanceBookingDays: number;

  // 운영 시간 정보
  operatingHours: {
    is24Hour: boolean;
    availableRanges: Array<{
      start: string; // "HH:MM"
      end: string;   // "HH:MM"
    }>;
    excludedStart: string | null;
    excludedEnd: string | null;
  };

  // 방 목록
  rooms: Array<{
    id: string;
    name: string;
    roomNumber: number;
    projector: boolean;
    aircon: boolean;
    facilities?: string | null; // 시설정보 (쉼표 구분 태그)
    projectorModel?: { name: string; model: string } | null;
    airconModel?: { name: string; model: string } | null;
  }>;

  // 예약/사용중/휴무 데이터 (예외만)
  blockedSlots: Array<{
    roomId: string | null; // null = 전체 룸 적용 (휴무)
    startTime: string; // ISO8601
    endTime: string;   // ISO8601
    reason: 'reserved' | 'in_use' | 'closed';
  }>;
}

export interface Reservation {
  id: number;
  storeId: string;
  roomId?: string;
  customerName: string;
  customerPhone: string;
  customerEmail?: string;
  reservationDate: string;
  startTime: string;
  gameCount: number;
  totalMinutes: number;
  status: 'PENDING' | 'CONFIRMED' | 'CANCELLED' | 'COMPLETED' | 'NO_SHOW';
  createdAt: string;
  updatedAt: string;
  room?: {
    id: string;
    name: string;
    roomNumber: number;
  };
}

// CCTV 스틸컷 관련 타입
export interface Stillcut {
  id: number;
  roomId: string;
  gameSessionId?: number;
  imageUrl: string;
  thumbnailUrl?: string;
  captureType: 'periodic' | 'event' | 'manual';
  capturedAt: string;
  createdAt: string;
}

export interface StillcutsResponse {
  stillcuts: Stillcut[];
  pagination: {
    total: number;
    limit: number;
    offset: number;
  };
}
